// navigation-context.tsx
'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigationScroll } from '@/hooks/use-navigation-scroll';

interface NavigationContextType {
  isNavigationVisible: boolean; // theo scroll
  navigationHeight: number;
  forceHidden: boolean; // ẩn bằng prop
  setForceHidden: (hidden: boolean) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const { isNavigationVisible } = useNavigationScroll();
  const hideCountRef = useRef(0);
  const [forceHidden, setForceHiddenState] = useState(false);

  // Counter-based: nav stays hidden until ALL hiders have released it.
  // Prevents race conditions when multiple sheets/modals overlap.
  const setForceHidden = useCallback((hidden: boolean) => {
    if (hidden) {
      hideCountRef.current += 1;
      setForceHiddenState(true);
    } else {
      hideCountRef.current = Math.max(0, hideCountRef.current - 1);
      if (hideCountRef.current === 0) {
        setForceHiddenState(false);
      }
    }
  }, []);

  const navigationHeight = 64; // 4rem = 64px

  return (
    <NavigationContext.Provider
      value={{
        isNavigationVisible,
        navigationHeight,
        forceHidden,
        setForceHidden,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    return {
      isNavigationVisible: false,
      navigationHeight: 64,
      forceHidden: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setForceHidden: () => {},
    };
  }
  return context;
}
